import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { IoTrashOutline as DeleteIcon } from 'react-icons/io5';
import { defineMessages, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';
import styles from '../IntegrationsListPage.css';
import HubspotPropertyAutocomplete from './HubspotPropertyAutocomplete';

const messages = defineMessages({
  survey: {
    id: 'IntegrationsSettings.HubspotSurvey',
    defaultMessage: 'Survey',
  },
  hubspotPropertyPrefix: {
    id: 'IntegrationsSettings.HubspotPropertyPrefix',
    defaultMessage: 'Property prefix',
  },
  hubspotProperty: {
    id: 'IntegrationsSettings.HubspotProperty',
    defaultMessage: 'Hubspot Property',
  },
  field: {
    id: 'IntegrationsSettings.Field',
    defaultMessage: 'Field',
  },
  addProperty: {
    id: 'IntegrationsSettings.AddProperty',
    defaultMessage: 'Add Property',
  },
  removeProperty: {
    id: 'IntegrationsSettings.RemoveProperty',
    defaultMessage: 'Remove',
  },
  additionalQuestion: {
    id: 'IntegrationsSettings.AdditionalQuestion',
    defaultMessage: 'Question {n}',
  },
  modalTitle: {
    id: 'IntegrationsSettings.EditHubspotSyncProperties',
    defaultMessage: 'Edit Hubspot Sync Properties',
  },
  cancel: {
    id: 'Confirm.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Confirm.Save',
    defaultMessage: 'Save',
  },
  score: {
    id: 'IntegrationsSettings.syncProperties.score',
    defaultMessage: 'Score',
  },
  comment: {
    id: 'IntegrationsSettings.syncProperties.comment',
    defaultMessage: 'Comment',
  },
  permalink: {
    id: 'IntegrationsSettings.syncProperties.permalink',
    defaultMessage: 'Permalink',
  },
  responded_at: {
    id: 'IntegrationsSettings.syncProperties.responded_at',
    defaultMessage: 'Responded at',
  },
  response_updated_at: {
    id: 'IntegrationsSettings.syncProperties.response_updated_at',
    defaultMessage: 'Response updated at',
  },
  group: {
    id: 'IntegrationsSettings.syncProperties.group',
    defaultMessage: 'Group',
  },
  survey_type: {
    id: 'IntegrationsSettings.syncProperties.survey_type',
    defaultMessage: 'Survey type',
  },
  survey_id: {
    id: 'IntegrationsSettings.syncProperties.survey_id',
    defaultMessage: 'Survey ID',
  },
  survey_name: {
    id: 'IntegrationsSettings.syncProperties.survey_name',
    defaultMessage: 'Survey Name',
  },
  propertiesToSend: {
    id: 'IntegrationsSettings.PropertiesToSend',
    defaultMessage: 'Properties to send',
  },
  noPropertiesAdded: {
    id: 'IntegrationsSettings.noPropertiesAdded',
    defaultMessage: 'No properties added',
  },
});

const defaultProperties = [
  'score',
  'comment',
  'permalink',
  'responded_at',
  'response_updated_at',
  'group',
  'survey_type',
  'survey_id',
  'survey_name',
];

function SyncPropertiesModal({ open, onClose, syncSurveyIdx, onSave }) {
  const { formatMessage } = useIntl();
  const { integration, surveys } = useOutletContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedSyncSurvey, setSelectedSyncSurvey] = useState(
    integration.syncSurveys?.[syncSurveyIdx],
  );

  const selectedSurvey = useMemo(
    () => surveys.find(s => s.id === selectedSyncSurvey?.survey),
    [surveys, selectedSyncSurvey],
  );

  React.useEffect(() => {
    setSelectedSyncSurvey(integration.syncSurveys?.[syncSurveyIdx]);
  }, [integration.syncSurveys, syncSurveyIdx]);

  const propertyPrefix = selectedSyncSurvey?.propertyPrefix || 'promoterninja';

  // Handlers
  const setSyncSurveyProps = props => {
    setSelectedSyncSurvey(prev => ({ ...prev, ...props }));
  };

  // Build property name options
  const defaultPropertyOptions = defaultProperties.map(name => ({
    value: name,
    label: formatMessage(messages[name]) || name,
    questionText: null,
    isAdditional: false,
  }));
  const additionalQuestionOptions = (selectedSurvey?.additionalQuestions || []).map((q, idx) => ({
    value: q._id,
    label: `${formatMessage(messages.additionalQuestion, { n: idx + 1 })}`,
    questionText: q.text,
    isAdditional: true,
    questionId: q._id,
  }));
  const propertyNameOptions = [...defaultPropertyOptions, ...additionalQuestionOptions];
  const allPossibleNames = [
    ...defaultProperties,
    ...(selectedSurvey?.additionalQuestions?.map(q => q._id) || []),
  ];
  const usedNames =
    selectedSyncSurvey?.syncProperties?.map(p => p.additionalQuestion || p.name) || [];
  const canAddProperty = usedNames.length < allPossibleNames.length;

  const handlePropNameChange = propIdx => event => {
    const newValue = event.target.value;
    const isAdditional = additionalQuestionOptions.some(opt => opt.value === newValue);
    setSyncSurveyProps({
      syncProperties: selectedSyncSurvey.syncProperties.map((p, i) => {
        if (i !== propIdx) return p;
        if (isAdditional) {
          return { ...p, additionalQuestion: newValue, name: undefined };
        }
        return { ...p, name: newValue, additionalQuestion: undefined };
      }),
    });
  };
  const handleRemoveProp = propIdx => () => {
    setSyncSurveyProps({
      syncProperties: selectedSyncSurvey.syncProperties.filter((_, i) => i !== propIdx),
    });
  };
  const handleAddProp = () => {
    // Find first unused default property
    const newName = defaultProperties.find(n => !usedNames.includes(n));
    if (newName) {
      setSyncSurveyProps({
        syncProperties: [...selectedSyncSurvey.syncProperties, { name: newName, label: '' }],
      });
      return;
    }
    // Find first unused additional question
    if (selectedSurvey?.additionalQuestions?.length) {
      for (let i = 0; i < selectedSurvey.additionalQuestions.length; i += 1) {
        const qId = selectedSurvey.additionalQuestions[i]._id;
        if (!usedNames.includes(qId)) {
          setSyncSurveyProps({
            syncProperties: [
              ...selectedSyncSurvey.syncProperties,
              { additionalQuestion: qId, label: '' },
            ],
          });
          return;
        }
      }
    }
  };

  if (!selectedSyncSurvey) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{formatMessage(messages.modalTitle)}</DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: isMobile ? 'block' : 'flex', gap: 2, mb: 2 }}>
          <FormControl variant="standard" sx={{ minWidth: 220, flex: 1, mb: isMobile ? 2 : 0 }}>
            <InputLabel htmlFor={`select-survey-${selectedSyncSurvey.survey}`}>
              {formatMessage(messages.survey)}
            </InputLabel>
            <Select
              variant="standard"
              id={`select-survey-${selectedSyncSurvey.survey}`}
              value={selectedSyncSurvey.survey || ''}
              onChange={e => setSyncSurveyProps({ survey: e.target.value })}
              autoWidth
              required
              aria-label={formatMessage(messages.survey)}
            >
              {surveys.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            required
            id={`propertyPrefix-${selectedSyncSurvey.survey}`}
            value={propertyPrefix}
            onChange={e => setSyncSurveyProps({ propertyPrefix: e.target.value })}
            variant="standard"
            label={formatMessage(messages.hubspotPropertyPrefix)}
            fullWidth
            sx={{ minWidth: 220, flex: 1 }}
            aria-label={formatMessage(messages.hubspotPropertyPrefix)}
          />
        </Box>
        <Box className={styles.syncSurveysSectionTitle}>
          {formatMessage(messages.propertiesToSend)}
        </Box>
        <Box className={styles.syncSurveysSection}>
          <Box className={styles.syncSurveysAddBtn}>
            <Button
              onClick={handleAddProp}
              color="primary"
              variant="text"
              startIcon={<AddIcon />}
              disabled={!canAddProperty}
              aria-label={formatMessage(messages.addProperty)}
              className={styles.syncSurveysAddBtnInner}
            >
              {formatMessage(messages.addProperty)}
            </Button>
          </Box>
          <Card variant="outlined" className={styles.syncSurveysTable}>
            <Box className={styles.syncSurveysHeader}>
              <Box className={styles.syncSurveysHeaderSurvey}>{formatMessage(messages.field)}</Box>
              <Box className={styles.syncSurveysHeaderHsProperty}>
                {formatMessage(messages.hubspotProperty)}
              </Box>
              <Box className={styles.syncSurveysHeaderActionsHs} />
            </Box>
            <Box>
              {selectedSyncSurvey.syncProperties.length === 0 ? (
                <Box className={styles.syncSurveysEmptyState}>
                  <Typography variant="body2" color="textSecondary">
                    {formatMessage(messages.noPropertiesAdded)}
                  </Typography>
                </Box>
              ) : (
                selectedSyncSurvey.syncProperties?.map((prop, propIdx) => {
                  const defaultLabel = `Promoter Ninja ${selectedSurvey?.name || ''} ${(prop.name || prop.additionalQuestion)?.replaceAll('_', ' ')}`;
                  return (
                    <Box
                      key={prop.name + prop.additionalQuestion}
                      className={styles.syncSurveysRow}
                    >
                      <FormControl
                        variant="standard"
                        className={styles.syncSurveysRowSurvey}
                        sx={{ pb: 1 }}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Select
                            labelId={`prop-name-label-${propIdx}`}
                            value={prop.additionalQuestion || prop.name}
                            onChange={handlePropNameChange(propIdx)}
                            aria-label={formatMessage(messages.hubspotProperty)}
                            fullWidth
                            MenuProps={{ disablePortal: true }}
                            input={<Input disableUnderline />}
                          >
                            {propertyNameOptions.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 220 }}>
                                  <Box
                                    sx={{
                                      maxWidth: 200,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {opt.label}
                                  </Box>
                                  {opt.questionText && (
                                    <Tooltip title={opt.questionText} placement="right" arrow>
                                      <InfoOutlinedIcon
                                        fontSize="small"
                                        color="action"
                                        style={{ marginLeft: 8 }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      </FormControl>
                      <Box className={styles.syncSurveysRowHsProperty} sx={{ pb: 1 }}>
                        <HubspotPropertyAutocomplete
                          value={prop.hsProperty || ''}
                          onChange={(_, newValue) => {
                            setSyncSurveyProps({
                              syncProperties: selectedSyncSurvey.syncProperties.map((p, i) =>
                                i === propIdx ? { ...p, hsProperty: newValue?.name } : p,
                              ),
                            });
                          }}
                          defaultLabel={defaultLabel}
                        />
                      </Box>
                      <Box className={styles.syncSurveysRowActionsHs}>
                        <IconButton
                          color="error"
                          onClick={handleRemoveProp(propIdx)}
                          size="small"
                          sx={{
                            ml: isMobile ? 0 : 1,
                            alignSelf: isMobile ? 'flex-start' : 'center',
                          }}
                          aria-label={formatMessage(messages.removeProperty)}
                          title={formatMessage(messages.removeProperty)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel)}</Button>
        <Button
          onClick={() => {
            onSave(selectedSyncSurvey);
            onClose();
          }}
          variant="contained"
        >
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SyncPropertiesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  syncSurveyIdx: PropTypes.number,
  onSave: PropTypes.func.isRequired,
};

SyncPropertiesModal.defaultProps = {
  syncSurveyIdx: undefined,
};

export default SyncPropertiesModal;
